/**
 * Container for a single role archetype chip in the BASIC filter section's
 * 2-column role grid. Wraps the onToggle callback with useCallback to give
 * the chip a stable onPress reference, and renders the shared SelectableChip
 * molecule (SE-5527) — the former local View and its 4-way theme hex
 * dictionary (and its dynamic-style waiver) are gone; semantic tokens are
 * theme-aware on their own. The flex-1 wrapper keeps the chip filling half
 * the grid row, as the old View's flex-1 style did.
 * @module features/scouting/components/MobileFilterSheet/MobileRoleChip/MobileRoleChipContainer
 */

import React, { memo, useCallback } from "react";

import { Box } from "@/components/atoms/Box";
import SelectableChip from "@/components/molecules/SelectableChip";

/** Props for MobileRoleChipContainer */
interface MobileRoleChipContainerProps {
  /** Unique ID for this role option */
  readonly id: string;
  /** Display label for the role */
  readonly label: string;
  /** Whether this role is currently selected */
  readonly isSelected: boolean;
  /** Callback when the role is toggled -- receives the role ID */
  readonly onToggle: (id: string) => void;
  /** Test identity forwarded to the chip (e2e). */
  readonly testID?: string;
}

/**
 * Wraps the toggle callback for a single role chip
 * @param root0 - Component props
 * @param root0.id - Role ID
 * @param root0.label - Role label
 * @param root0.isSelected - Whether role is selected
 * @param root0.testID - Test identity forwarded to the chip
 * @param root0.onToggle - Toggle callback
 */
const MobileRoleChipContainer = ({
  id,
  label,
  isSelected,
  onToggle,
  testID,
}: MobileRoleChipContainerProps) => {
  const handlePress = useCallback(() => {
    onToggle(id);
  }, [onToggle, id]);

  return (
    <Box className="flex-1">
      <SelectableChip
        label={label}
        isSelected={isSelected}
        onPress={handlePress}
        shape="chip"
        testID={testID}
      />
    </Box>
  );
};

MobileRoleChipContainer.displayName = "MobileRoleChipContainer";

export default memo(MobileRoleChipContainer);
